/* eslint camelcase: 0 */
import api, { rawClient } from './api';
import {
    ActionPlanSnapshot,
    ReportRequestSnapshot,
    ReportSnapshot,
    ScreeningSnapshot,
} from '../models';
import { decamelizeKeys, keyBy } from '../lib';
import {
    CountResponse,
    PaginatedRequest,
    PaginatedResponse,
} from '../lib/pagination';
import { ArchivalAction } from '../constants/filter';
import { getBackVersion } from '../config';

type ActionPlanResponse = {
    availableDetectivePlans: ActionPlanSnapshot[];
    availableSpcPlan: ActionPlanSnapshot | null;
};

type RawScreeningResponse = Omit<
    ScreeningSnapshot,
    | 'reports'
    | 'reportRequests'
    | 'availableSpcPlan'
    | 'availableDetectivePlans'
> & {
    reports: ReportSnapshot[];
    reportRequests: ReportRequestSnapshot;
} & ActionPlanResponse;

type ScreeningResponse = Omit<
    ScreeningSnapshot,
    'availableSpcPlan' | 'availableDetectivePlans'
> &
    ActionPlanResponse;

type RawScreeningPaginatedResponse = {
    screenings: RawScreeningResponse[];
} & PaginatedResponse;

type ScreeningPaginatedResponse = {
    screenings: ScreeningResponse[];
} & PaginatedResponse;

type ScreeningRequestFilter = {
    doc?: string;
    foundName?: string;
    docType?: string;
    isArchived?: boolean;
};

const toSnapshot = ({
    reports,
    reportRequests,
    ...response
}: RawScreeningResponse): ScreeningResponse => {
    return {
        ...response,
        reports: keyBy(reports, x => x.id),
        reportRequests: keyBy(reportRequests, x => x.id),
    };
};

const mapToSnapshot = (response: RawScreeningResponse[]) =>
    response.map(toSnapshot);

const getPaginatedScreenings = (
    params: PaginatedRequest & ScreeningRequestFilter = {}
): Promise<ScreeningPaginatedResponse> => {
    return api
        .request<RawScreeningPaginatedResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/screenings`,
            params: decamelizeKeys(params),
        })
        .then(response => ({
            screenings: mapToSnapshot(response.screenings),
            after: response.after,
            count: response.count,
        }));
};

const getScreeningsCount = (
    params: PaginatedRequest & ScreeningRequestFilter
): Promise<CountResponse> => {
    return api.request<CountResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/screenings/count`,
        params: decamelizeKeys(params),
    });
};

const getScreening = (screeningId: string): Promise<ScreeningResponse> => {
    return api
        .request<RawScreeningResponse>({
            method: 'GET',
            url: `${getBackVersion()}/agent/screenings/` + screeningId,
        })
        .then(toSnapshot);
};

const createScreeningByDoc = (doc: string): Promise<ScreeningResponse> => {
    return api
        .request<RawScreeningResponse>({
            method: 'POST',
            url: `${getBackVersion()}/agent/screenings/cpf`,
            data: {
                cpf: doc,
            },
        })
        .then(toSnapshot);
};

const archiveScreening = (
    screeningId: string,
    action: ArchivalAction
): Promise<void> => {
    return api.request({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/screenings/${screeningId}`,
        params: { action },
    });
};

const downloadReport = (screeningId: string): Promise<void> => {
    return rawClient.download({
        url: `${getBackVersion()}/agent/screenings/${screeningId}/pdf`,
    });
};

export default {
    archiveScreening,
    createScreeningByDoc,
    getPaginatedScreenings,
    getScreening,
    getScreeningsCount,
    downloadReport,
};

export type {
    ScreeningPaginatedResponse,
    ScreeningRequestFilter,
    ScreeningResponse,
};
